import { Button, Dropdown } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { GlobalLoadingBar } from 'modules/page';
import { AddMatchingIcon, ShowMatchingIcon } from 'modules/sprites';
import { FC, memo, useCallback } from 'react';
import { netSuiteSourceMatchingLineState } from 'shared/components/(matchings)';

import { Content, useDeleteMatching } from './components';
import {
    useIsAddingMatchingAllowed,
    useMatchingInfo,
    useNetSuiteMatchingInfoDropdown,
    useSourceLine,
} from './NetSuiteMatchingInfo.hooks';
import { NetSuiteMatchingInfoProps } from './NetSuiteMatchingInfo.types';

export const NetSuiteMatchingInfo: FC<NetSuiteMatchingInfoProps> = memo((props) => {
    const {
        request,
        lineType,
        lineNumber,
        isMatched,
        isRequestForm = false,
        onSuccessDeleteMatching,
        removeDraftMatching,
    } = props;

    const setNetSuiteSourceMatchingLineState = useSetAtom(netSuiteSourceMatchingLineState);

    const { isOpened, onDropdownClose, toggleIsOpened } = useNetSuiteMatchingInfoDropdown(false);

    const sourceLine = useSourceLine(request, lineType, lineNumber, isRequestForm);

    const { matchingInfo, isFetching } = useMatchingInfo({
        request,
        isOpened,
        isDraftMatching: sourceLine?.isDraftMatching ?? false,
        targetRequestId: sourceLine?.targetRequestId,
        lineNumber,
        lineType,
        itemId: sourceLine && 'item' in sourceLine ? sourceLine.item?.id : undefined,
        accountId: sourceLine && 'account' in sourceLine ? sourceLine.account?.id : undefined,
        categoryId: sourceLine && 'category' in sourceLine ? sourceLine.category?.id : undefined,
    });

    const { isLoadingDeleteMatching } = useDeleteMatching(lineType);

    const isAddingMatchingAllowed = useIsAddingMatchingAllowed(request, sourceLine, isRequestForm);

    const showAddMatchingPopup = useCallback(() => {
        if (!sourceLine) {
            return;
        }

        setNetSuiteSourceMatchingLineState({
            ...sourceLine,
            lineType,
        });
    }, [sourceLine, lineType, setNetSuiteSourceMatchingLineState]);

    if (!isMatched) {
        return isAddingMatchingAllowed ? (
            <Button icon variant='text' color='blue100' justifyContent='end' onClick={showAddMatchingPopup}>
                <AddMatchingIcon />
            </Button>
        ) : null;
    }

    return (
        <Dropdown
            open={isOpened}
            onOpen={toggleIsOpened}
            width='max-content'
            display='inline'
            maxWidth={800}
            maxHeight={450}
            activator={
                <Button icon variant='text' color='blue100'>
                    <ShowMatchingIcon />
                </Button>
            }
        >
            <>
                <GlobalLoadingBar isLoading={(isFetching || isLoadingDeleteMatching) && isOpened} />

                {matchingInfo && (
                    <Content
                        request={request}
                        lineType={lineType}
                        lineNumber={lineNumber}
                        matchingInfo={matchingInfo}
                        isFetching={isFetching}
                        isRequestForm={isRequestForm}
                        onDropdownClose={onDropdownClose}
                        onSuccessDeleteMatching={onSuccessDeleteMatching}
                        removeDraftMatching={removeDraftMatching}
                    />
                )}
            </>
        </Dropdown>
    );
});

NetSuiteMatchingInfo.displayName = 'NetSuiteMatchingInfo';
